import { ChangeEvent, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    InputAdornment,
    Link,
    Paper,
    Skeleton,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip
} from '@mui/material';
import {
    ColumnFilter,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    PaginationState,
    useReactTable
} from '@tanstack/react-table';
import { Match } from 'effect';
import { isNonEmptyArray } from 'effect/Array';

import { PageHeading } from '../../components/PageHeading';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { usePrefetchScanType, useScanTypes } from '../../services/scan-types/queries';
import { ScanType } from '../../services/scan-types/schemas';
import { type AdminUser, type SiteAdminUser } from '../../types/User';
import { getLocationUrlParamByUser } from './ScanTypesOverview.utils';

const Layout = styled(Box)`
    display: grid;
    grid-template-rows: auto auto 1fr;
    gap: 16px;
`;

const TableCellHead = styled(TableCell)`
    font-weight: bold;
    font-size: 1rem;
`;

const DEFAULT_PAGE_SIZE = 10;

const ROWS_PER_PAGE_OPTIONS = [DEFAULT_PAGE_SIZE, 25, 50, 100];

const DEBOUNCE_DELAY_MS = 300;

const columnHelper = createColumnHelper<ScanType>();

const NameLink = ({ name, scanTypeId }: { name: string; scanTypeId: number }) => {
    const prefetchScanType = usePrefetchScanType(scanTypeId);

    return (
        <Link component={RouterLink} to={`/scans/types/${scanTypeId}`} onMouseOver={prefetchScanType}>
            {name}
        </Link>
    );
};

const columns = [
    columnHelper.accessor('name', {
        header: 'Name',
        cell: (cellContext) => <NameLink name={cellContext.getValue()} scanTypeId={cellContext.row.original.id} />,
        size: 250
    }),
    columnHelper.accessor('groupType', {
        header: 'Category',
        cell: (cellContext) => <Chip label={cellContext.getValue()} size="small" />,
        size: 150
    }),
    columnHelper.accessor('active', {
        header: 'Enabled',
        cell: (cellContext) =>
            cellContext.getValue() ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />
    }),
    columnHelper.accessor('locations', {
        header: 'Locations',
        cell: (cellContext) =>
            Match.value(cellContext.getValue()).pipe(
                Match.when(
                    (value) => isNonEmptyArray(value),
                    ([firstLocation, ...restLocations]) => (
                        <Box display="flex" flexDirection="row" gap="8px">
                            <Chip label={firstLocation} size="small" />

                            {!!restLocations.length && (
                                <Tooltip title={restLocations.join(', ')}>
                                    <Chip label={`+${restLocations.length}`} size="small" />
                                </Tooltip>
                            )}
                        </Box>
                    )
                ),
                Match.orElse(() => null)
            )
    }),
    columnHelper.accessor('createdByUsername', {
        header: 'Created by',
        cell: (cellContext) =>
            Match.value(cellContext.getValue()).pipe(
                Match.when('', () => '-'),
                Match.orElse((value) => value)
            )
    })
];

type TableVisibilityState = {
    [K in keyof ScanType]?: boolean;
};

type ScanTypesOverviewProps = {
    user: AdminUser | SiteAdminUser;
};

export const ScanTypesOverview = ({ user }: ScanTypesOverviewProps) => {
    const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
    const [nameFilter, setNameFilter] = useState({ id: 'name', value: '' } satisfies ColumnFilter);

    const debouncedNameFilter = useDebouncedValue(nameFilter.value, DEBOUNCE_DELAY_MS);
    const location = getLocationUrlParamByUser(user);

    const scanTypes = useScanTypes({
        params: {
            page: pagination.pageIndex + 1,
            show: pagination.pageSize,
            location: location !== '' ? location : undefined,
            name: debouncedNameFilter !== '' ? debouncedNameFilter : undefined
        }
    });

    const table = useReactTable({
        data: scanTypes.data?.items ?? [],
        columns,
        rowCount: scanTypes.data?.count,
        state: {
            pagination: pagination,
            columnFilters: [nameFilter],
            columnVisibility: {
                locations: user.type === 'ADMIN'
            } satisfies TableVisibilityState
        },
        manualPagination: true,
        manualFiltering: true,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel()
    });

    const totalColumns = table.getHeaderGroups().reduce((acc, headerGroup) => acc + headerGroup.headers.length, 0);

    return (
        <Layout>
            <Breadcrumbs>
                <Link component={RouterLink} to="/scans/types" underline="hover" color="inherit">
                    <PageHeading>Scan Types</PageHeading>
                </Link>
            </Breadcrumbs>

            <TextField
                label="Scan name"
                placeholder="Search for scan name"
                sx={{ width: '300px' }}
                value={nameFilter.value}
                onChange={(event) => setNameFilter({ ...nameFilter, value: event.target.value } satisfies ColumnFilter)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <SearchIcon />
                        </InputAdornment>
                    )
                }}
                size="small"
            />

            <TableContainer component={Paper}>
                <Table aria-label="Scan types">
                    <TableHead sx={{ backgroundColor: '#f8f8f8' }}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableCellHead
                                        key={header.id}
                                        sx={{ width: `${header.getSize()}px`, fontWeight: '600', color: '#696969' }}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableCellHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>

                    <TableBody>
                        {Match.value(scanTypes).pipe(
                            Match.when({ isLoading: true }, () =>
                                Array.from({ length: DEFAULT_PAGE_SIZE }).map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell colSpan={totalColumns}>
                                            <Skeleton variant="rounded" height={40} data-testid="skeleton-loader" />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ),
                            Match.when({ data: Match.defined }, () =>
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id} sx={{ width: `${cell.column.getSize()}px` }}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ),
                            Match.when({ error: { message: Match.string } }, ({ error }) => (
                                <TableRow>
                                    <TableCell colSpan={totalColumns}>
                                        <Box display="flex" flexDirection="column" gap="16px" alignItems="flex-start">
                                            <Button variant="contained" onClick={() => scanTypes.refetch()}>
                                                Retry
                                            </Button>

                                            <Alert severity="error">{error.message}</Alert>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )),
                            Match.orElse(() => null)
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={totalColumns}>
                                <TablePagination
                                    component="div"
                                    page={table.getState().pagination.pageIndex}
                                    count={table.getRowCount()}
                                    onPageChange={(_, newPage) =>
                                        setPagination(
                                            (prevState) =>
                                                ({
                                                    ...prevState,
                                                    pageIndex: newPage
                                                }) satisfies typeof pagination
                                        )
                                    }
                                    rowsPerPage={table.getState().pagination.pageSize}
                                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                    onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) =>
                                        setPagination(
                                            (prevState) =>
                                                ({
                                                    ...prevState,
                                                    pageIndex: 0,
                                                    pageSize: parseInt(event.target.value, 10)
                                                }) satisfies typeof pagination
                                        )
                                    }
                                />
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Layout>
    );
};
